<template>
  <div>
    <validation-observer ref="observer" tag="div">
      <!--
        The keys keep Vue from reusing the request step's e-mail input as the
        code input when the page moves on. Buefy's b-input reads autocomplete
        only once, at creation, so a reused input kept autocomplete="username"
        and Safari saved the new password with the code as the username.
      -->
      <form
        v-if="page === PAGE.REQUEST"
        key="request"
        @submit.prevent="doSubmit"
      >
        <p class="is-size-3 has-text-weight-bold">
          {{ $t("mp:forgotten_password.title") }}
        </p>

        <p class="info has-text-grey-icon">
          {{ $t("mp:forgotten_password.subtitle") }}
        </p>

        <BInputWithValidation
          v-model="user"
          rules="required"
          :label="$t('mp:forgotten_password.input.phone_or_email.label')"
          label-position="on-border"
          autocomplete="username"
          class="mb-5"
        />

        <b-field>
          <b-button
            type="is-blue"
            native-type="submit"
            :loading="reqLoading"
            expanded
            >{{ $t("mp:forgotten_password.button.next") }}
          </b-button>
        </b-field>
      </form>

      <template v-else-if="page === PAGE.SOLUTION">
        <template v-if="looksLikeAnEmail && !this.forcePasswordChange">
          <p class="is-size-3 has-text-weight-bold">
            {{ $t("mp:forgotten_password.email.title") }}
          </p>

          <p class="info has-text-grey-icon">
            {{ $t("mp:forgotten_password.email.subtitle") }}
          </p>
        </template>

        <template v-else-if="forcePasswordChange">
          <p class="is-size-3 has-text-weight-bold">
            {{ $t("mp:forgotten_password.password_change_required.title") }}
          </p>

          <p class="info has-text-grey-icon">
            <template v-if="maskedContact">
              {{
                $t("mp:forgotten_password.password_change_required.sent_to", {
                  contact: maskedContact,
                })
              }}
            </template>
            <template v-else>
              {{ $t("mp:forgotten_password.password_change_required.code") }}
            </template>
          </p>
        </template>

        <template v-else>
          <p class="is-size-3 has-text-weight-bold">
            {{ $t("mp:forgotten_password.phone.title") }}
          </p>
        </template>

        <!--
          The code and the new password are in separate forms on purpose.
          Safari reads a form with a new-password field as a sign-up form and
          offers the e-mail for the text field before the password, ignoring
          autocomplete="one-time-code" on it. In a form of its own the code
          field is no username candidate, so iOS offers the SMS code there.
        -->
        <form key="code" class="code-form" @submit.prevent="codeEntered">
          <BInputWithValidation
            :label="$t('mp:forgotten_password.input.security_code.label')"
            rules="required"
            label-position="on-border"
            input-id="one-time-code"
            autocomplete="one-time-code"
            inputmode="numeric"
            v-model="code"
          />
        </form>

        <form key="password" ref="passwordForm" @submit.prevent="doSubmit">
          <!--
            Hands password managers the e-mail / phone the merchant logged in
            with, so the new password is saved under the right account.
          -->
          <input
            type="text"
            name="username"
            autocomplete="username"
            :value="user"
            readonly
            tabindex="-1"
            aria-hidden="true"
            class="autofill-username"
          />

          <b-field
            :label="$t('mp:forgotten_password.input.new_password.label')"
            rules="required"
            label-position="on-border"
          >
            <PasswordCheckerInput
              v-model="newPassword"
              @validated="passwordValid = $event"
            />
          </b-field>

          <b-field>
            <b-button
              type="is-blue"
              native-type="submit"
              expanded
              :loading="finishLoading"
              >{{ $t("mp:forgotten_password.button.submit") }}
            </b-button>
          </b-field>
        </form>

        <div
          v-if="forcePasswordChange && looksLikeAnEmail"
          class="has-text-right"
        >
          <b-button
            type="is-text"
            :loading="resendLoading"
            :disabled="resendCooldown > 0"
            @click="resendByEmail"
          >
            {{ $t("mp:forgotten_password.button.resend_email") }}
            <span v-if="resendCooldown > 0">({{ resendCooldown }})</span>
          </b-button>
        </div>
      </template>
    </validation-observer>
  </div>
</template>
<script>
const PAGE = {
  REQUEST: 0,
  SOLUTION: 1,
};

// Survives a reload of the forced password change page, which used to drop
// the merchant back on the empty "forgotten password" form.
const FORCED_CHANGE_STORAGE_KEY = "fintrous_instacash_mp_forced_pwd_change";
const FORCED_CHANGE_MAX_AGE_MS = 30 * 60 * 1000;
const RESEND_COOLDOWN_SECONDS = 60;

const saveForcedChange = (data) => {
  try {
    sessionStorage.setItem(
      FORCED_CHANGE_STORAGE_KEY,
      JSON.stringify({ ...data, savedAt: Date.now() })
    );
  } catch (e) {
    // storage unavailable (private mode etc.), the page still works without it
  }
};

const loadForcedChange = () => {
  try {
    const data = JSON.parse(sessionStorage.getItem(FORCED_CHANGE_STORAGE_KEY));
    if (data && Date.now() - data.savedAt < FORCED_CHANGE_MAX_AGE_MS) {
      return data;
    }
  } catch (e) {
    // ignore, treated as nothing saved
  }
  return null;
};

const clearForcedChange = () => {
  try {
    sessionStorage.removeItem(FORCED_CHANGE_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

import i18n from "@/plugins/i18n";
import { mapGetters } from "vuex";
import { generateNonce } from "@/utils/util";
import Vue from "vue";

export default {
  name: "PasswordReset",
  title: "mp:page.forgot_password.title",
  components: {
    BInputWithValidation: () =>
      import("@/components/input/BInputWithValidation.vue"),
    PasswordCheckerInput: () =>
      import("@/components/input/PasswordCheckerInput.vue"),
  },
  data() {
    return {
      PAGE,
      user: "",
      nonce: generateNonce(),
      code: "",
      chId: "",
      newPassword: "",
      passwordValid: false,
      page: PAGE.REQUEST,
      reqLoading: false,
      finishLoading: false,
      forcePasswordChange: false,
      maskedContact: "",
      resendLoading: false,
      resendCooldown: 0,
      resendTimer: null,
    };
  },
  beforeDestroy() {
    clearInterval(this.resendTimer);
  },
  mounted() {
    if (this.loggedIn) {
      this.$router.push("/user/profile");
    }
  },
  computed: {
    ...mapGetters({
      loggedIn: "session/isReady",
      secondFactor: "session/secondFactor",
    }),
    looksLikeAnEmail() {
      return this.user.includes("@");
    },
  },
  created() {
    // parse URL from the verification e-mail
    const code = this.$route.query.code;
    if (code) {
      this.code = code;
      this.page = PAGE.SOLUTION;
    }
    // Required password change, straight from the login or restored after a reload
    let forced = null;
    if (this.secondFactor) {
      forced = {
        id: this.secondFactor.id,
        nonce: this.secondFactor.nonce,
        user: this.secondFactor.user || "",
        maskedContact: this.secondFactor.maskedContact || "",
      };
      saveForcedChange(forced);
      this.$store.dispatch("session/deleteSecondFactor");
    } else if (!code) {
      forced = loadForcedChange();
    }
    if (forced) {
      this.page = PAGE.SOLUTION;
      this.chId = forced.id;
      this.nonce = forced.nonce;
      this.user = forced.user;
      this.maskedContact = forced.maskedContact;
      this.forcePasswordChange = true;
    }
  },
  methods: {
    startResendCooldown() {
      this.resendCooldown = RESEND_COOLDOWN_SECONDS;
      clearInterval(this.resendTimer);
      this.resendTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer);
        }
      }, 1000);
    },
    async resendByEmail() {
      try {
        this.resendLoading = true;
        const response = await this.$store.dispatch(
          "session/requestPwdChange",
          {
            user: this.user,
            nonce: this.nonce,
          }
        );
        this.chId = response.id;
        this.code = "";
        saveForcedChange({
          id: this.chId,
          nonce: this.nonce,
          user: this.user,
          maskedContact: this.maskedContact,
        });
        this.startResendCooldown();
        this.$buefy.toast.open({
          message: i18n.t("mp:forgotten_password.resend.success", {
            contact: response.maskedContact || this.user,
          }),
          type: "is-success",
        });
      } catch (ex) {
        console.error(ex);
        this.$buefy.toast.open({
          message: i18n.t("mp:forget.error.general"),
          type: "is-danger",
        });
      }
      this.resendLoading = false;
    },
    // Enter in the code field: go on to the new password, or submit when the
    // password is already there.
    codeEntered() {
      if (this.newPassword) {
        this.doSubmit();
        return;
      }
      const passwordInput = this.$refs.passwordForm?.querySelector(
        'input[type="password"]'
      );
      if (passwordInput) {
        passwordInput.focus();
      }
    },
    finishErrorMessage(ex) {
      const status = ex?.response?.status;
      const error = ex?.response?.data?.error;
      if (status === 400) {
        return i18n.t("mp:forget.new_password_cannot_be_old_password");
      }
      if (status === 422 && error === "CODE_REGENERATED") {
        return i18n.t("mp:forget.error.code_regenerated");
      }
      if (status === 422) {
        return i18n.t("mp:forget.error.invalid_code");
      }
      if (status === 410) {
        return i18n.t("mp:forget.error.code_expired");
      }
      return i18n.t("mp:forget.error.general");
    },
    async doSubmit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      if (!this.user.includes("@")) {
        let tel = this.user.replace(/ /g, "");
        tel = tel.replace("+", "");
        if (tel.length === 9) {
          // se 06, se 36
          tel = "36" + tel;
        }
        if (tel.startsWith("06")) {
          // 06 -> 36
          tel = "36" + tel.substring(2);
        }
        Vue.set(this, "user", tel);
      }

      if (this.page === PAGE.REQUEST) {
        try {
          this.reqLoading = true;
          let reqResponse = await this.$store.dispatch(
            "session/requestPwdChange",
            {
              user: this.user,
              nonce: this.nonce,
            }
          );
          this.chId = reqResponse.id;

          this.reqLoading = false;
          this.page = PAGE.SOLUTION;
        } catch (ex) {
          console.error(ex);
          this.$buefy.toast.open({
            message: i18n.t("mp:forget.error.general"),
            type: "is-danger",
          });
          this.reqLoading = false;
        }
      } else {
        if (!this.passwordValid) return;

        try {
          this.finishLoading = true;

          let chngResponse = await this.$store.dispatch(
            "session/finishPwdChange",
            {
              id: this.chId,
              nonce: this.nonce,
              code: this.code,
              newPassword: this.newPassword,
            }
          );
          let redirect = await this.$store.dispatch(
            "profile/setStatus",
            chngResponse
          );

          clearForcedChange();
          this.$router.push(redirect);
          this.finishLoading = false;
        } catch (ex) {
          console.error(ex);
          this.finishLoading = false;
          this.$buefy.toast.open({
            message: this.finishErrorMessage(ex),
            type: "is-danger",
            duration: 6000,
          });
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.info {
  margin-top: -1rem;
  margin-bottom: 2rem !important;
  max-width: 75%;
}

.code-form {
  margin-bottom: 0.75rem;
}

.autofill-username {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
</style>
